/**
 * A conversa com o portal.
 *
 * ── Por que a sessão é um COOKIE, e não um cabeçalho ─────────────────
 *
 * A tela da equipe guarda o token e o manda em `Authorization`. Aqui
 * isso não funcionaria, e o motivo é o PDF.
 *
 * Um `<iframe>` que mostra o laudo, um link «baixar», uma aba nova
 * aberta no celular: nenhum deles carrega cabeçalho. Com o token só em
 * memória, restariam dois caminhos, e os dois são piores — puxar o PDF
 * inteiro para dentro da página como `blob` (que come memória do
 * celular e quebra o visualizador nativo do iOS), ou pôr o segredo na
 * própria URL, onde ele fica no histórico, no log do servidor e no
 * grupo de WhatsApp para onde alguém reenviar o endereço.
 *
 * O cookie resolve os três casos de uma vez. Ele é `HttpOnly` — o
 * JavaScript desta página não consegue lê-lo, então um script injetado
 * não rouba a sessão —, `Secure`, `SameSite=Strict` (site nenhum
 * consegue fazer o navegador usá-lo) e de SESSÃO: morre quando a aba
 * fecha, que é o comportamento certo num portal aberto no computador
 * compartilhado da obra.
 *
 * O efeito colateral é que esta página não sabe se há sessão. Ela
 * pergunta: `/eu` responde 200 com o nome, ou 401.
 */

export class ErroDoPortal extends Error {
  constructor(msg: string, readonly status: number) { super(msg); }
}

type Ouvinte = () => void;
const ouvintes = new Set<Ouvinte>();

/** Avisa a aplicação quando a sessão cai, para ela voltar ao início. */
export function aoPerderSessao(f: Ouvinte): () => void {
  ouvintes.add(f);
  return () => ouvintes.delete(f);
}

interface Opcoes { metodo?: string; corpo?: unknown; sinal?: AbortSignal }

export async function api<T>(caminho: string, o: Opcoes = {}): Promise<T> {
  let r: Response;
  try {
    r = await fetch(`/portal-api${caminho}`, {
      method: o.metodo ?? 'GET',
      /* Explícito, ainda que seja o padrão: o cookie É a sessão. */
      credentials: 'same-origin',
      headers: o.corpo ? { 'content-type': 'application/json' } : undefined,
      body: o.corpo ? JSON.stringify(o.corpo) : undefined,
      signal: o.sinal,
    });
  } catch (e) {
    /* Requisição cancelada não é falha: a tela mudou de rota. */
    if ((e as Error)?.name === 'AbortError') throw e;
    throw new ErroDoPortal(
      'Não consegui falar com o portal. Confira sua conexão e tente de novo.',
      0);
  }

  if (r.status === 401) {
    /*
     * Perder a sessão não é um erro a mostrar no meio da tela: é uma
     * volta ao começo. Sem este aviso, o cliente ficaria olhando uma
     * lista vazia sem saber que precisa entrar de novo.
     */
    ouvintes.forEach((f) => f());
  }

  const texto = await r.text();
  const corpo = texto ? seguroJson(texto) : null;

  if (!r.ok) {
    const m = corpo as { mensagem?: string; message?: string } | null;
    throw new ErroDoPortal(
      m?.mensagem ?? m?.message ?? 'Não foi possível concluir.', r.status);
  }
  return corpo as T;
}

function seguroJson(t: string): unknown {
  try { return JSON.parse(t); } catch { return null; }
}

/** O endereço de um documento — usado no visor e nos dois botões. */
export const enderecoDoDocumento = (
  tipo: string, id: string, baixar = false,
) => `/portal-api/documento/${tipo}/${id}${baixar ? '?baixar=1' : ''}`;

/**
 * Formatos brasileiros, que é o padrão do sistema inteiro — no FUSO DA CASA
 * (UI-T-01, onda 329, 15/10/2026).
 *
 * Sem `timeZone`, o dia era o do navegador do cliente; e o dia-só
 * (`AAAA-MM-DD`) era lido como meia-noite UTC. Um laudo entregue às 10h de
 * 08/10 aparecia no Portal como entregue em «07/10/2026». As mesmas regras do
 * `web/src/formato.ts`: o dia-só se escreve pelo texto; o carimbo, em Brasília.
 */
const FUSO = 'America/Sao_Paulo';

export function dataBr(v: string | null | undefined): string {
  if (!v) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR', { timeZone: FUSO });
}

export function dataHoraBr(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—'
    : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: FUSO });
}

/** O dia (`AAAA-MM-DD`) em Brasília — para agrupar e para o `<input type="date">`. */
export function diaNaCasa(v: string | null | undefined): string {
  if (!v) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSO, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(d);
  const pega = (t: string) => p.find((x) => x.type === t)?.value ?? '';
  return `${pega('year')}-${pega('month')}-${pega('day')}`;
}

export function tamanho(bytes: string | number | null | undefined): string {
  const n = Number(bytes ?? 0);
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1048576).toFixed(1).replace('.', ',')} MB`;
}
